import * as React from "react";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import Headline from "../../components/Headline";
import WideCard from "../../components/WideCard";
import SmartInput from "../../components/SmartInput";
import { Radio, RadioGroup } from "@material-ui/core";
import SmartButton from "../../components/SmartButton";
import { MainLayout } from "../../components/Layout";
import { useForm } from "react-hook-form";
import { PHONE_NUMBER } from "../../generated/lib-domain/regex";
import "./styles.scss";
import { PREFECTURES } from "../../generated/lib-domain/prefectures";
import FormErrorMessage from "../../components/FormErrorMessage";
import { Gender } from "../../generated/lib-domain/gender";
import PostalCodeButton from "../../components/PostalCodeButton";

interface RegisterPaidPageProps {
    onSubmit: (data) => void;
    isLoading: boolean;
}

const birthday = (): {
    DATE: { id: string; text: string }[];
    MONTH: { id: string; text: string }[];
    YEAR: { id: string; text: string }[];
} => {
    const thisYear = new Date().getFullYear();
    const year = [...Array(80)].map((_, index) => {
        const value = String(thisYear - 20 - index);
        return { id: value, text: value + "年" };
    });
    const month = [...Array(12)].map((_, index) => {
        return { id: String(1 + index), text: String(1 + index) + "月" };
    });
    const date = [...Array(31)].map((_, index) => {
        return { id: String(1 + index), text: String(1 + index) + "日" };
    });
    return { YEAR: year, MONTH: month, DATE: date };
};
const removeHyphen = (e: React.ChangeEvent<HTMLInputElement>): void => {
    e.target.value = e.target.value.replace(/-/g, "");
};
// eslint-disable-next-line @typescript-eslint/no-var-requires
const newTabIcon = require("../../assets/img/new-tab.svg").default;
const schema = yup.object().shape({
    nickname: yup
        .string()
        .required("ニックネームを入力してください")
        .max(24, "ニックネームは24文字以内で入力してください"),
    givenName: yup
        .string()
        .required("名前を入力してください")
        .max(24, "名前は24文字以内で入力してください"),
    familyName: yup
        .string()
        .required("苗字を入力してください")
        .max(24, "苗字は24文字以内で入力してください"),
    givenNameKana: yup
        .string()
        .required("名前のフリガナを入力してください")
        .max(24, "名前のフリガナは24文字以内で入力してください")
        .matches(
            /^[ァ-ンヴー]*$/,
            "名前のフリガナはカタカナで入力してください"
        ),
    familyNameKana: yup
        .string()
        .required("苗字のフリガナを入力してください")
        .max(24, "苗字のフリガナは24文字以内で入力してください")
        .matches(
            /^[ァ-ンヴー]*$/,
            "苗字のフリガナはカタカナで入力してください"
        ),
    year: yup.string().required(),
    month: yup.string().required(),
    date: yup.string().required(),
    gender: yup.string().required("性別を選択してください"),
    phone: yup
        .string()
        .required("電話番号が入力されていません")
        .min(10, "電話番号は10桁から15桁で入力してください")
        .max(15, "電話番号は10桁から15桁で入力してください")
        .matches(PHONE_NUMBER, "電話番号は半角数字で入力してください"),
    zip: yup
        .string()
        .required("郵便番号が入力されていません")
        .matches(/^[\d]{7}$/, "郵便番号は7桁の半角数字でご記入ください"),
    pref: yup.number(),
    city: yup
        .string()
        .required("市区町村が入力されていません")
        .max(48, "市区町村は48文字以内で入力してください"),
    address: yup
        .string()
        .required("番地が入力されていません")
        .max(25, "番地は25文字以内で入力してください"),
    building: yup.string().max(25, "建物名は25文字以内で入力してください")
});

const RegisterPaidPage = (props: RegisterPaidPageProps): JSX.Element => {
    const {
        register,
        errors,
        handleSubmit,
        formState: { isDirty, isValid },
        getValues,
        setValue
    } = useForm({
        mode: "onChange",
        resolver: yupResolver(schema)
    });
    const enterAddressFromPostalCode = (address: {
        region_id: number;
        region: string;
        locality: string;
        street: string;
        extended: string;
    }) => {
        if (address.region_id) {
            setValue("pref", address.region_id);
            setValue("city", address.locality + address.street);
            setValue("address", address.extended);
        }
    };
    return (
        <MainLayout isBurgerHidden={true} autoWrapWideCard={false}>
            <Headline>会員登録</Headline>
            <WideCard>
                <div className="registerPaid__text">
                    会員様の情報を登録してください。
                </div>
                <form
                    onSubmit={handleSubmit(props.onSubmit)}
                    className="registerPaid__form"
                >
                    <div className="registerPaid__form-item">
                        <label>ニックネーム</label>
                        <SmartInput
                            name="nickname"
                            register={register}
                            error={errors.nickname}
                            placeholder="全角、半角24文字以内"
                        />
                    </div>
                    <div className="registerPaid__form-item">
                        <div>
                            <label>氏名</label>
                        </div>
                        <div className="registerPaid__name">
                            <div className="name-item">
                                <SmartInput
                                    name="familyName"
                                    register={register}
                                    placeholder="姓"
                                />
                            </div>
                            <div>
                                <SmartInput
                                    name="givenName"
                                    register={register}
                                    placeholder="名"
                                    showPrivateMark={true}
                                />
                            </div>
                        </div>
                        {errors.familyName && (
                            <FormErrorMessage
                                message={errors.familyName.message}
                            />
                        )}
                        {errors.givenName && (
                            <FormErrorMessage
                                message={errors.givenName.message}
                            />
                        )}
                    </div>
                    <div className="registerPaid__form-item">
                        <div>
                            <label>フリガナ</label>
                        </div>
                        <div className="registerPaid__name">
                            <div className="name-item">
                                <SmartInput
                                    name="familyNameKana"
                                    register={register}
                                    placeholder="セイ"
                                />
                            </div>
                            <div>
                                <SmartInput
                                    name="givenNameKana"
                                    register={register}
                                    placeholder="メイ"
                                    showPrivateMark={true}
                                />
                            </div>
                        </div>
                        {errors.familyNameKana && (
                            <FormErrorMessage
                                message={errors.familyNameKana.message}
                            />
                        )}
                        {errors.givenNameKana && (
                            <FormErrorMessage
                                message={errors.givenNameKana.message}
                            />
                        )}
                    </div>
                    <div className="registerPaid__form-item">
                        <label>生年月日</label>
                        <div className="registerPaid__birthday-input">
                            <select
                                name="year"
                                ref={register}
                                id="year-select"
                                className="registerPaid__input-base"
                                defaultValue=""
                                required
                            >
                                <option
                                    value=""
                                    disabled
                                    className="placeholder"
                                >
                                    年
                                </option>
                                {birthday().YEAR.map((p) => {
                                    return (
                                        <option
                                            value={p.id}
                                            key={"year" + p.id}
                                        >
                                            {p.text}
                                        </option>
                                    );
                                })}
                            </select>
                            <select
                                name="month"
                                ref={register}
                                id="month-select"
                                className="registerPaid__input-base"
                                defaultValue=""
                                required
                            >
                                <option
                                    value=""
                                    disabled
                                    className="placeholder"
                                >
                                    月
                                </option>
                                {birthday().MONTH.map((p) => {
                                    return (
                                        <option
                                            value={p.id}
                                            key={"month" + p.id}
                                        >
                                            {p.text}
                                        </option>
                                    );
                                })}
                            </select>
                            <select
                                name="date"
                                ref={register}
                                id="date-select"
                                className="registerPaid__input-base"
                                defaultValue=""
                                required
                            >
                                <option
                                    value=""
                                    disabled
                                    className="placeholder"
                                >
                                    日
                                </option>
                                {birthday().DATE.map((p) => {
                                    return (
                                        <option
                                            value={p.id}
                                            key={"date" + p.id}
                                        >
                                            {p.text}
                                        </option>
                                    );
                                })}
                            </select>
                        </div>
                    </div>
                    <div className="registerPaid__form-item">
                        <label>性別</label>
                        <div className="registerPaid__form-gender">
                            <RadioGroup row>
                                <label htmlFor="gm">
                                    <Radio
                                        inputRef={register}
                                        value={Gender.Male}
                                        id="gm"
                                        name="gender"
                                        color="primary"
                                    />
                                    男性
                                </label>
                                <label htmlFor="gf">
                                    <Radio
                                        inputRef={register}
                                        value={Gender.Female}
                                        id="gf"
                                        name="gender"
                                        color="primary"
                                    />
                                    女性
                                </label>
                            </RadioGroup>
                        </div>
                    </div>
                    <div className="registerPaid__form-item">
                        <label>携帯番号</label>
                        <SmartInput
                            name="phone"
                            register={register}
                            error={errors.phone}
                            type="tel"
                            placeholder="ハイフン無しで入力"
                            onBlur={removeHyphen}
                            showPrivateMark={true}
                        />
                    </div>
                    <section className="registerPaid__address-info">
                        <h3>契約書面のお届け先</h3>
                        <p>
                            有料契約された場合、契約書面をお送りしますのでお届け先住所をご記入ください。
                            <br />
                            なお、契約書面はプライバシーを考慮して、無地の封筒に封入し、差出人は個人名で送付致します。
                        </p>
                    </section>
                    <div className="registerPaid__form-item">
                        <label>郵便番号</label>
                        <SmartInput
                            name="zip"
                            register={register}
                            error={errors.zip}
                            placeholder="1230000（ハイフン無し）"
                            className="address-input"
                            showPrivateMark={true}
                        />
                    </div>
                    <PostalCodeButton
                        handleOnClick={enterAddressFromPostalCode}
                        postalCode={getValues("zip")}
                    />
                    <div className="registerPaid__form-item">
                        <label>住所</label>
                        <div className="registerPaid__prefecture-select registerPaid__address-item">
                            <select name="pref" ref={register()} required>
                                <option value="">都道府県</option>
                                {PREFECTURES.map((p) => {
                                    return (
                                        <option value={p.id} key={p.id}>
                                            {p.text}
                                        </option>
                                    );
                                })}
                            </select>
                        </div>
                        <div className="registerPaid__address-item">
                            <SmartInput
                                name="city"
                                register={register}
                                error={errors.city}
                                placeholder="市区町村"
                            />
                        </div>
                        <div className="registerPaid__address-item">
                            <SmartInput
                                name="address"
                                register={register}
                                error={errors.address}
                                placeholder="番地"
                            />
                        </div>
                        <div className="registerPaid__address-item">
                            <SmartInput
                                name="building"
                                register={register}
                                error={errors.building}
                                placeholder="建物名、号室"
                            />
                        </div>
                    </div>
                    <p className="registerPaid__terms">
                        20歳以上かつ独身であり、
                        <a
                            href="https://p-a.app/pages/terms/"
                            target="_blank"
                            rel="noreferrer"
                        >
                            利用規約
                            <img src={newTabIcon} />
                        </a>
                        および
                        <br />
                        <a
                            href="https://tameny.jp/privacy/"
                            target="_blank"
                            rel="noreferrer"
                        >
                            プライバシーポリシー
                            <img src={newTabIcon} />
                        </a>
                        に同意しますか
                    </p>
                    <SmartButton
                        type="submit"
                        disabled={!isDirty || !isValid}
                        isLoading={props.isLoading}
                    >
                        同意して登録する
                    </SmartButton>
                </form>
            </WideCard>
        </MainLayout>
    );
};

export default RegisterPaidPage;
